import React, { useContext } from 'react';

import { FormattedMessage } from 'react-intl';

import { BodyCopy } from '@xingternal/typography';
import { Button } from '@xingternal/button';
import {
  EditButton,
  UpsellBannerVertical,
  PageContext,
} from '@xingternal/pages-common';
import { getTrackingUrl } from '@xingternal/pages-tracking';

import Awards from './Awards';
import GalleryPreview from './GalleryPreview';
import { GalleryItemsType, Badges } from '../types';

import styles from './AboutUs.css';

type AboutUsProps = {
  pageSlug: string;
  headline?: string;
  summary: string;
  hasSubpage: boolean;
  galleryItems: GalleryItemsType;
  badges: Badges;
  upsellActive?: boolean;
  isEditing: boolean;
};

const AboutUs = ({
  upsellActive,
  headline,
  summary,
  pageSlug,
  hasSubpage,
  galleryItems,
  badges,
  isEditing,
}: AboutUsProps) => {
  const { pageContext } = useContext(PageContext);
  return (
    <div className={styles.wrapper} data-testid={'ABOUT_US_MODULE'}>
      <div className={styles.aboutUsWrapper}>
        {upsellActive ? (
          <div className={styles.upsellLayout}>
            <UpsellBannerVertical
              headerCopyKey="EP_UPSELL_PROFILE_MULTIMEDIA_HEADER"
              bodyCopyKey="EP_UPSELL_PROFILE_MULTIMEDIA_BODY"
              moduleName="aboutusmodule"
            />
          </div>
        ) : (
          <GalleryPreview pageSlug={pageSlug} galleryItems={galleryItems} />
        )}
        <div className={styles.summaryAwardsWrapper}>
          <div className={styles.summary} data-cy={'aboutUsText'}>
            {headline && (
              <BodyCopy
                fontWeight="bold"
                size={'xlarge'}
                className={styles.headline}
                data-testid={'ABOUTUS_HEADLINE'}
              >
                {headline}
                {isEditing && pageSlug && (
                  <EditButton
                    size={'small'}
                    showIcon={true}
                    className={styles.descriptionEdit}
                    data-cy="EDIT_BUTTON_ON_ABOUT_US_DESC_TITLE"
                    data-testid="EDIT_BUTTON_ON_ABOUT_US_DESC_TITLE"
                    to={`${pageContext?.basePath}/${pageSlug}/edit/aboutUsDescription?type=short`}
                  />
                )}
              </BodyCopy>
            )}
            <BodyCopy
              noMargin
              size={'small'}
              className={styles.text}
              data-testid={'ABOUTUS_DESC'}
            >
              {summary}
              {!headline && isEditing && pageSlug && (
                <EditButton
                  size={'small'}
                  showIcon={true}
                  className={styles.descriptionEdit}
                  data-testid="edit-aboutus-description"
                  data-cy="EDIT_BUTTON_ON_ABOUT_US_DESC_DETAIL"
                  to={`${pageContext?.basePath}/${pageSlug}/edit/aboutUsDescription?type=short`}
                />
              )}
            </BodyCopy>
            {hasSubpage && (
              <span className={styles.buttonWrapper}>
                <Button
                  variant={'tertiary'}
                  size={'small'}
                  to={getTrackingUrl({
                    to: `${pageContext?.basePath}/${pageSlug}/about_us`,
                    trackingKey: 'discoverMore',
                  })}
                  className={styles.button}
                >
                  <FormattedMessage
                    id={'EP_ABOUT_US_DISCOVER_CTA'}
                    defaultMessage={'EP_ABOUT_US_DISCOVER_CTA'}
                  />
                </Button>
              </span>
            )}
          </div>
        </div>
      </div>
      {badges.length > 0 && (
        <div>
          <Awards pageSlug={pageSlug} badges={badges} />
        </div>
      )}
    </div>
  );
};

export default AboutUs;
